import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { ChevronLeft, HelpCircle, Loader2 } from 'lucide-react'
import './Scores.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import MainURL from '../../MainURL'
import { drumtaleAdminSessionAtom, drumtaleAdminTokenAtom } from '../../AtomStore'
import { gradeAccessPolicy, openPageSpecification } from '../shared/pageSpecification'
import {
  Score,
  defaultScoreFormatsByDifficulty,
  difficultyLabels,
  scoreCategories,
  scoreFormats,
  scoresDataModelGuide,
  scoresImplementationGuide,
  scoresSpecification,
  scoresStateDefinitions,
} from './scoresData'
import ScorePreviewModal from './ScorePreviewModal'

const TOAST_DURATION_MS = 2400
const SAVE_LOADING_MIN_MS = 1000
const DIFFICULTY_FILE_KEYS = ['easy', 'normal', 'hard'] as const

interface LevelState {
  freeAllowed: boolean
  formats: Set<string>
  abcText: string
  midUrl: string | null
  jsonUrl: string | null
}

interface SavedLevel {
  difficultyCode?: string
  freeAllowed?: boolean
  formats?: string[]
  abcText?: string
  midUrl?: string | null
  jsonUrl?: string | null
}

function resolveAssetUrl(path?: string | null) {
  if (!path) return ''
  if (/^(https?:|blob:|data:)/.test(path)) return path
  return `${MainURL}${path}`
}

function conventionalLevelAssetPaths(scoreId: string | number, index: number) {
  const key = DIFFICULTY_FILE_KEYS[index]
  return {
    midUrl: `/uploads/scores/midi/${scoreId}_${key}.mid`,
    jsonUrl: `/uploads/scores/json/${scoreId}_${key}.json`,
  }
}

/** SPA fallback(HTML)과 실제 파일을 구분해서 자산 존재 여부를 확인 */
async function probePublicAsset(path: string): Promise<boolean> {
  try {
    const response = await fetch(resolveAssetUrl(path), {
      method: 'GET',
      headers: { Range: 'bytes=0-0' },
    })
    if (!response.ok) return false
    const contentType = (response.headers.get('content-type') || '').toLowerCase()
    if (contentType.includes('text/html')) return false
    return true
  } catch {
    return false
  }
}

async function resolveLevelAssetPresence(
  scoreId: string | number,
  index: number,
  saved?: SavedLevel,
): Promise<{ midUrl: string | null; jsonUrl: string | null }> {
  const expected = conventionalLevelAssetPaths(scoreId, index)
  const midUrl = saved?.midUrl || null
  const jsonUrl = saved?.jsonUrl || null

  const [midExists, jsonExists] = await Promise.all([
    midUrl ? Promise.resolve(true) : probePublicAsset(expected.midUrl),
    jsonUrl ? Promise.resolve(true) : probePublicAsset(expected.jsonUrl),
  ])

  return {
    midUrl: midUrl || (midExists ? expected.midUrl : null),
    jsonUrl: jsonUrl || (jsonExists ? expected.jsonUrl : null),
  }
}

function createInitialLevelState(): LevelState[] {
  return difficultyLabels.map((level, index) => ({
    freeAllowed: level === 'Easy',
    formats: new Set([defaultScoreFormatsByDifficulty[index]]),
    abcText: '',
    midUrl: null,
    jsonUrl: null,
  }))
}

function ScoreRegister() {
  const navigate = useNavigate()
  const { scoreId } = useParams()
  const isEdit = Boolean(scoreId)
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)
  const adminToken = useAtomValue(drumtaleAdminTokenAtom)

  const [title, setTitle] = useState('')
  const [artist, setArtist] = useState('')
  const [genre, setGenre] = useState(scoreCategories[0])
  const [visibility, setVisibility] = useState<'공개' | '미공개'>('공개')
  const [thumbnailPreview, setThumbnailPreview] = useState('')
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [audioLabel, setAudioLabel] = useState('')
  const [audioObjectUrl, setAudioObjectUrl] = useState('')
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [existingAudioUrl, setExistingAudioUrl] = useState('')

  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [levels, setLevels] = useState<LevelState[]>(createInitialLevelState)

  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewLevelIndex, setPreviewLevelIndex] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingScore, setIsLoadingScore] = useState(isEdit)

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAdminSessionActive) navigate('/')
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    if (!isEdit || !scoreId || !adminToken) return

    const loadScore = async () => {
      setIsLoadingScore(true)
      try {
        const response = await fetch(`${MainURL}/admin/scores/${scoreId}`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        })
        const data = await response.json().catch(() => null)
        if (!response.ok) {
          showToast(data?.message || '악보를 불러오지 못했습니다.')
          navigate('/admin/scores')
          return
        }

        const score = data?.score
        if (!score) {
          showToast('악보를 찾을 수 없습니다.')
          navigate('/admin/scores')
          return
        }

        setTitle(score.title || '')
        setArtist(score.artist || '')
        setGenre(score.genre && scoreCategories.includes(score.genre) ? score.genre : (score.genre || scoreCategories[0]))
        setVisibility(score.hidden || score.status === '미공개' ? '미공개' : '공개')
        setThumbnailPreview(resolveAssetUrl(score.thumbnailUrl))
        setExistingAudioUrl(resolveAssetUrl(score.audioUrl))
        setAudioLabel(score.audioUrl ? '음원 연결됨' : '')

        const nextLevels = await Promise.all(difficultyLabels.map(async (label, index) => {
          const saved = (score.levels || []).find((level: SavedLevel) => level.difficultyCode === label)
          const assets = await resolveLevelAssetPresence(scoreId, index, saved)
          return {
            freeAllowed: saved?.freeAllowed ?? label === 'Easy',
            formats: new Set<string>(saved?.formats?.length ? saved.formats : [defaultScoreFormatsByDifficulty[index]]),
            abcText: saved?.abcText || '',
            midUrl: assets.midUrl,
            jsonUrl: assets.jsonUrl,
          }
        }))
        setLevels(nextLevels)
      } catch (error) {
        showToast('악보를 불러오지 못했습니다.')
        navigate('/admin/scores')
      } finally {
        setIsLoadingScore(false)
      }
    }

    loadScore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, isEdit, scoreId])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current)
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current)
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(scoresSpecification, scoresDataModelGuide, scoresImplementationGuide, {
      gradeAccessPolicy,
      stateDefinitions: scoresStateDefinitions,
    })
    if (!opened) showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
  }

  const updateLevel = (index: number, patch: Partial<LevelState>) => {
    setLevels((previous) => previous.map((level, i) => (i === index ? { ...level, ...patch } : level)))
  }

  const toggleLevelFormat = (index: number, format: string) => {
    setLevels((previous) => previous.map((level, i) => {
      if (i !== index) return level
      const nextFormats = new Set(level.formats)
      if (nextFormats.has(format)) nextFormats.delete(format)
      else nextFormats.add(format)
      return { ...level, formats: nextFormats }
    }))
  }

  const handleThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setThumbnailFile(file)
    setThumbnailPreview(URL.createObjectURL(file))
  }

  const handleAudioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setAudioFile(file)
    setAudioLabel(file.name)
    setAudioObjectUrl(URL.createObjectURL(file))
  }

  const handleAbcFileChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => updateLevel(index, { abcText: String(reader.result ?? '') })
    reader.readAsText(file)
  }

  const validate = (): string | null => {
    if (!title.trim()) return '노래 제목을 입력해 주세요.'
    if (!artist.trim()) return '가수명을 입력해 주세요.'
    const filledIndexes = levels
      .map((level, index) => ({ level, index }))
      .filter(({ level }) => level.abcText.trim())
    if (filledIndexes.length === 0) {
      return 'Easy / Normal / Hard 중 최소 1개 난이도의 ABC 코드를 입력해 주세요.'
    }
    for (const { level, index } of filledIndexes) {
      if (level.formats.size === 0) {
        return `${difficultyLabels[index]} 난이도의 악보 형식을 1개 이상 선택해 주세요.`
      }
    }
    return null
  }

  const handleSaveAndExit = async () => {
    const error = validate()
    if (error) {
      showToast(error)
      return
    }

    const loadingStartedAt = Date.now()
    const waitMinLoading = async () => {
      const elapsed = Date.now() - loadingStartedAt
      const remain = SAVE_LOADING_MIN_MS - elapsed
      if (remain > 0) await new Promise((resolve) => window.setTimeout(resolve, remain))
    }
    const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

    setIsSaving(true)
    try {
      const url = isEdit ? `${MainURL}/admin/scores/${scoreId}` : `${MainURL}/admin/scores`
      const method = isEdit ? 'PUT' : 'POST'
      const maxAttempts = 3
      let response: Response | null = null
      let data: any = null
      let lastNetworkError: unknown = null

      const buildFormData = () => {
        const formData = new FormData()
        formData.append('title', title.trim())
        formData.append('artist', artist.trim())
        formData.append('genre', genre)
        formData.append('visibility', visibility)
        formData.append('levels', JSON.stringify(difficultyLabels.map((label, index) => ({
          difficultyCode: label,
          freeAllowed: levels[index].freeAllowed,
          formats: [...levels[index].formats],
          abcText: levels[index].abcText.trim(),
        }))))
        if (thumbnailFile) formData.append('thumbnail', thumbnailFile)
        if (audioFile) formData.append('audio', audioFile)
        return formData
      }

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          response = await fetch(url, {
            method,
            headers: { Authorization: `Bearer ${adminToken}` },
            body: buildFormData(),
          })
          data = await response.json().catch(() => null)
          lastNetworkError = null
          if (response.ok || response.status < 500 || attempt === maxAttempts) break
          await sleep(attempt * 600)
        } catch (err) {
          lastNetworkError = err
          if (attempt === maxAttempts) break
          await sleep(attempt * 600)
        }
      }

      await waitMinLoading()

      if (lastNetworkError || !response) {
        showToast(isEdit ? '악보 수정에 실패했습니다. 네트워크 상태를 확인해 주세요.' : '악보 저장에 실패했습니다. 네트워크 상태를 확인해 주세요.')
        return
      }

      if (!response.ok) {
        showToast(data?.message || (isEdit ? '악보 수정에 실패했습니다.' : '악보 저장에 실패했습니다.'))
        return
      }

      const filledLevels = difficultyLabels.filter((_, index) => levels[index].abcText.trim())
      const savedScore: Score = {
        scoreId: data?.scoreId ? Number(data.scoreId) : (scoreId ? Number(scoreId) : undefined),
        title: title.trim(),
        artist: artist.trim(),
        genre,
        status: visibility,
        hidden: visibility === '미공개',
        levels: filledLevels.length ? filledLevels : [...difficultyLabels],
        freeLevels: difficultyLabels.filter((_, index) => levels[index].freeAllowed && levels[index].abcText.trim()),
        plays: 0,
        featured: '-',
        registered: '',
        lastPlayed: '-',
        updated: '',
      }

      navigate('/admin/scores', {
        state: {
          newScore: savedScore,
          isEdit,
          warning: data?.warning || null,
        },
      })
    } catch (err) {
      await waitMinLoading()
      showToast(isEdit ? '악보 수정에 실패했습니다. 네트워크 상태를 확인해 주세요.' : '악보 저장에 실패했습니다. 네트워크 상태를 확인해 주세요.')
    } finally {
      setIsSaving(false)
    }
  }

  const openPreview = () => {
    if (!levels[activeTabIndex].abcText.trim()) {
      showToast(`${difficultyLabels[activeTabIndex]} 난이도의 ABC 코드를 입력한 뒤 미리보기를 열어 주세요.`)
      return
    }
    setPreviewLevelIndex(activeTabIndex)
    setIsPreviewOpen(true)
  }

  const activeLevel = levels[activeTabIndex]
  const previewLevel = levels[previewLevelIndex]
  const previewFormat = useMemo(
    () => [...previewLevel.formats][0] || defaultScoreFormatsByDifficulty[previewLevelIndex],
    [previewLevel, previewLevelIndex],
  )

  return (
    <div className='Scores'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <button className='back-button' type='button' onClick={() => navigate('/admin/scores')}>
                <ChevronLeft size={16} />
                뒤로가기
              </button>
              <h1>악보관리</h1>
            </div>
          </header>

          <section className='view active'>
            <div className='detail-stack' aria-busy={isLoadingScore}>
              {isLoadingScore ? (
                <>
                  <section className='detail-hero' aria-hidden>
                    <p className='eyebrow'>악보관리</p>
                    <span className='score-skeleton-line register-title' />
                    <span className='score-skeleton-line register-desc' />
                  </section>

                  <section className='panel compact-score-info score-register-skeleton' aria-hidden>
                    <div className='section-title'>
                      <span className='score-skeleton-line medium' />
                      <span className='score-skeleton-line short' />
                    </div>
                    <div className='score-basic-grid'>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <div key={`basic-skel-${index}`} className='score-register-skeleton-field'>
                          <span className='score-skeleton-line short' />
                          <span className='score-skeleton-block register-input' />
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className='panel score-register-skeleton' aria-hidden>
                    <div className='section-title'>
                      <span className='score-skeleton-line medium' />
                      <span className='score-skeleton-line short' />
                    </div>
                    <div className='score-tabs score-register-skeleton-tabs'>
                      {difficultyLabels.map((level) => (
                        <span key={`tab-skel-${level}`} className='score-skeleton-block register-tab' />
                      ))}
                    </div>
                    <article className='score-tab-panel active'>
                      <div className='level-card-head'>
                        <span className='score-skeleton-line medium' />
                        <div className='level-status-badges'>
                          <span className='score-skeleton-block register-badge' />
                          <span className='score-skeleton-block register-badge' />
                          <span className='score-skeleton-block register-badge' />
                        </div>
                      </div>
                      <span className='score-skeleton-block register-toggle' />
                      <div className='score-format-group'>
                        {scoreFormats.map((format) => (
                          <span key={`format-skel-${format}`} className='score-skeleton-block register-format' />
                        ))}
                      </div>
                      <div className='score-register-skeleton-field'>
                        <span className='score-skeleton-line short' />
                        <span className='score-skeleton-block register-input' />
                      </div>
                      <div className='score-register-skeleton-field'>
                        <span className='score-skeleton-line short' />
                        <span className='score-skeleton-block register-textarea' />
                      </div>
                    </article>
                  </section>

                  <div className='detail-footer' aria-hidden>
                    <span className='score-skeleton-line button' />
                    <span className='score-skeleton-line button' />
                    <span className='score-skeleton-line button' />
                  </div>
                </>
              ) : (
                <>
              <section className='detail-hero'>
                <p className='eyebrow'>악보관리</p>
                <h2>{isEdit ? `${title || '악보'} 수정` : '신규 악보 등록'}</h2>
                <p>곡 정보를 입력하고, 난이도별로 ABC 코드를 등록하면 저장 시 .mid와 JSON 파일이 자동 생성됩니다. ABC를 비운 난이도는 저장되지 않습니다.</p>
              </section>

              <section className='panel compact-score-info'>
                <div className='section-title'>
                  <h3>기본 정보</h3>
                  <span>최소 입력</span>
                </div>
                <div className='score-basic-grid'>
                  <label className='modal-field'>
                    노래 제목
                    <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder='노래 제목을 입력하세요' />
                  </label>
                  <label className='modal-field'>
                    가수명
                    <input value={artist} onChange={(event) => setArtist(event.target.value)} placeholder='가수명을 입력하세요' />
                  </label>
                  <label className='modal-field'>
                    음악 장르
                    <select value={genre} onChange={(event) => setGenre(event.target.value)}>
                      {scoreCategories.includes(genre) ? null : <option value={genre}>{genre}</option>}
                      {scoreCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </label>
                  <label className='modal-field'>
                    썸네일
                    <input type='file' accept='image/*' onChange={handleThumbnailChange} />
                    <span className='score-upload-preview'>
                      {thumbnailPreview ? <img src={thumbnailPreview} alt='선택한 썸네일' /> : '선택한 이미지가 목록에 표시됩니다.'}
                    </span>
                  </label>
                  <label className='modal-field'>
                    원곡 또는 반주 MP3
                    <input type='file' accept='audio/mp3,audio/*' onChange={handleAudioChange} />
                    <span className='score-upload-preview'>
                      {audioLabel ? `${audioLabel}` : '등록한 음원은 미리보기에서 자동 재생됩니다.'}
                    </span>
                  </label>
                  <label className='modal-field'>
                    사용자 노출
                    <select value={visibility} onChange={(event) => setVisibility(event.target.value as '공개' | '미공개')}>
                      <option value='공개'>공개</option>
                      <option value='미공개'>미공개</option>
                    </select>
                  </label>
                </div>
              </section>

              <section className='panel'>
                <div className='section-title'>
                  <h3>난이도별 악보</h3>
                  <span>입력한 난이도만 저장</span>
                </div>
                <div className='score-tabs' role='tablist'>
                  {difficultyLabels.map((level, index) => (
                    <button
                      key={level}
                      type='button'
                      className={`score-tab${index === activeTabIndex ? ' active' : ''}`}
                      onClick={() => setActiveTabIndex(index)}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <article className='score-tab-panel active'>
                  <div className='level-card-head'>
                    <h3>{difficultyLabels[activeTabIndex]}</h3>
                    <div className='level-status-badges'>
                      <span className={`badge ${activeLevel.abcText.trim() ? 'success' : ''}`}>
                        {activeLevel.abcText.trim() ? 'abc코드 등록됨' : 'abc코드 미등록'}
                      </span>
                      <span className={`badge ${activeLevel.midUrl ? 'success' : 'warning'}`}>
                        {activeLevel.midUrl ? '.mid 생성됨' : '.mid 미생성'}
                      </span>
                      <span className={`badge ${activeLevel.jsonUrl ? 'success' : 'warning'}`}>
                        {activeLevel.jsonUrl ? '.json 생성됨' : '.json 미생성'}
                      </span>
                    </div>
                  </div>
                  <label className='toggle score-free-toggle'>
                    <input
                      type='checkbox'
                      checked={activeLevel.freeAllowed}
                      onChange={(event) => updateLevel(activeTabIndex, { freeAllowed: event.target.checked })}
                    />
                    무료 회원 이용 허용
                  </label>
                  <div className='score-format-group' role='group' aria-label={`${difficultyLabels[activeTabIndex]} 악보 형식`}>
                    {scoreFormats.map((format) => (
                      <label key={format} className='score-format-option'>
                        <input
                          type='checkbox'
                          checked={activeLevel.formats.has(format)}
                          onChange={() => toggleLevelFormat(activeTabIndex, format)}
                        />
                        <span>{format}</span>
                      </label>
                    ))}
                  </div>
                  <div className='score-editor-grid'>
                    <label className='modal-field'>
                      ABC 파일 업로드
                      <input type='file' accept='.abc,.txt' onChange={(event) => handleAbcFileChange(activeTabIndex, event)} />
                    </label>
                  </div>
                  <label className='modal-field'>
                    ABC 코드 편집기
                    <textarea
                      value={activeLevel.abcText}
                      onChange={(event) => updateLevel(activeTabIndex, { abcText: event.target.value })}
                      placeholder='ABC 표기법으로 작성한 드럼 악보 코드를 붙여넣거나 파일을 업로드하세요.'
                    />
                  </label>
                </article>
              </section>

              <div className='detail-footer'>
                <button className='secondary-button' type='button' onClick={openPreview} disabled={isSaving}>미리보기</button>
                <button className='secondary-button' type='button' onClick={() => navigate('/admin/scores')} disabled={isSaving}>임시저장</button>
                <button className='primary-button' type='button' onClick={handleSaveAndExit} disabled={isSaving || isLoadingScore}>
                  {isSaving ? '저장 중...' : isEdit ? '수정 저장' : '저장 후 목록으로'}
                </button>
              </div>
                </>
              )}
            </div>
          </section>
        </main>
      </div>

      {isSaving ? (
        <div className='score-save-loading' role='alert' aria-live='assertive' aria-busy='true'>
          <div className='score-save-loading-card'>
            <Loader2 className='score-save-loading-spinner' size={36} aria-hidden />
            <h3>{isEdit ? '악보를 수정하는 중' : '악보를 저장하는 중'}</h3>
            <p>ABC 코드를 저장하고 .mid / JSON 파일을 생성합니다.<br />완료될 때까지 잠시만 기다려 주세요.</p>
          </div>
        </div>
      ) : null}

      {isPreviewOpen ? (
        <ScorePreviewModal
          title={title}
          artist={artist}
          level={difficultyLabels[previewLevelIndex]}
          format={previewFormat}
          abcText={previewLevel.abcText}
          audioSrc={audioObjectUrl || existingAudioUrl || undefined}
          onClose={() => setIsPreviewOpen(false)}
        />
      ) : null}

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default ScoreRegister
